import { z } from 'zod'
import i18next from 'i18next'
import { errorContainsInvalidItems, Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { CompiledQuery } from 'kysely'

import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { addUserMutationQuery } from './user/userCreate'
import { changeUserMutationQuery } from './user/userUpdate'
import { Role, isHigherRole, getRoleFromString } from '@/lib/role'
import { buildM2MLocalDBquery } from '@/lib/utils/m2mHelper'
import { withStage } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'
import { pick, filterPick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'
import { isExistAll } from '@/lib/relation-graph'
import { isWithinAvailableSelects } from '@/lib/zod/validators'

const TABLE_NAME = 'user'

const isUniqueEmail = async ({ id, email }: { id: string; email: string }): Promise<Boolean> => {
  const isExist = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom(TABLE_NAME)
      .select('email')
      .where('email', '=', email)
      .where('id', '!=', id)
      .execute()
  })
  if (isExist.length > 0) return false
  return true
}

const isLowerRole = (role: Role) => {
  const currentRole = useUserStore().role
  return isHigherRole(currentRole, role)
}

const userMutableSchema = z.object({
  name: z
    .string()
    .trim()
    .max(32, i18next.t('stages.labels.error.user.nameTooLong', { ns: 'translation' }))
    .min(2, i18next.t('stages.labels.error.user.nameTooShort', { ns: 'translation' })),
  email: z
    .string()
    .max(128, i18next.t('stages.labels.error.user.emailTooLong', { ns: 'translation' }))
    .email(i18next.t('stages.labels.error.user.invalidEmail', { ns: 'translation' })),
  role: z
    .nativeEnum(Role)
    .refine(isLowerRole, i18next.t('stages.labels.error.user.higherRole', { ns: 'translation' })),
  note: z
    .string()
    .max(5000, i18next.t('stages.labels.error.user.noteTooLong', { ns: 'translation' }))
    .nullish(),
  available: z.boolean().default(true),
  display_order: z
    .number()
    .int(i18next.t('stages.labels.error.user.notInteger', { ns: 'translation' }))
    .min(0, i18next.t('stages.labels.error.user.displayOrderNegative', { ns: 'translation' }))
    .default(0)
})

const userImmutableSchema = z.object({
  id: z.string().max(21),
  firebase_uid: z.string().nullable()
})

const userTableSchema = userMutableSchema.extend(userImmutableSchema.shape)

const userRelationSchema = z.object({
  departmentIds: z
    .array(z.string().max(21))
    .default([])
    .refine(
      isWithinAvailableSelects(10),
      i18next.t('stages.labels.error.user.max10Department', { ns: 'translation' })
    )
    .refine(isExistAll('department'), errorContainsInvalidItems),
  jobTitleIds: z
    .array(z.string().max(21))
    .default([])
    .refine(
      isWithinAvailableSelects(30),
      i18next.t('stages.labels.error.user.max30JobTitle', { ns: 'translation' })
    )
    .refine(isExistAll('job_title'), errorContainsInvalidItems),
  jobSkillIds: z
    .array(z.string().max(21))
    .default([])
    .refine(
      isWithinAvailableSelects(160),
      i18next.t('stages.labels.error.user.max160JobSkill', { ns: 'translation' })
    )
    .refine(isExistAll('job_skill'), errorContainsInvalidItems)
})

export const userSchema = userTableSchema
  .extend(userRelationSchema.shape)
  .refine(
    isUniqueEmail,
    i18next.t('stages.labels.error.user.duplicateEmail', { ns: 'translation' })
  )

export type User = z.input<typeof userSchema>
export type Relations = z.input<typeof userRelationSchema>
const userWithRelations = userTableSchema.merge(userRelationSchema)
export type UserWithRelations = z.input<typeof userWithRelations>

export class UserMutator extends Mutator<typeof userSchema> {
  static readonly stageableClassName = 'UserMutator'
  schema = userSchema
  table = 'user' as const

  static async load(id: string): Promise<Reactive<UserMutator>> {
    const userMutator = this.createInstance()
    const user = await this.getUserData(id)
    if (!user) {
      userMutator.context = {
        id: id,
        firebase_uid: null
      }
      // TODO: Initialize for select items(job_titles, job_skills)
      return userMutator
    }
    const departments = await this.getDepartments(id)
    const jobTitles = await this.getJobTitles(id)
    const jobSkills = await this.getJobSkills(id)

    userMutator.context = {
      id: id,
      firebase_uid: user.firebase_uid
    }

    userMutator.base = {
      id: user.id,
      name: user.name,
      email: user.email,
      role: getRoleFromString(user.role),
      departmentIds: departments.map((record) => record.department_id),
      jobTitleIds: jobTitles.map((record) => record.job_title_id),
      jobSkillIds: jobSkills.map((record) => record.job_skill_id),
      note: user.note,
      firebase_uid: user.firebase_uid,
      available: Boolean(user.available),
      display_order: user.display_order ?? 0
    }
    return userMutator
  }

  private static async getTenantData() {
    return DataBase.selectFrom('tenant').select(['firebase_tenant_id']).executeTakeFirst()
  }

  private static async getUserData(id: string) {
    return DataBase.selectFrom('user')
      .select([
        'user.id',
        'user.name',
        'user.email',
        'user.role',
        'user.firebase_uid',
        'available',
        'note',
        'display_order'
      ])
      .where('user.id', '=', id)
      .executeTakeFirst()
  }

  private static async getDepartments(id: string) {
    return DataBase.selectFrom('user_to_department')
      .select(['department_id'])
      .where('user_id', '=', id)
      .execute()
  }

  private static async getJobTitles(id: string) {
    return DataBase.selectFrom('user_to_job_title')
      .select(['job_title_id'])
      .where('user_id', '=', id)
      .execute()
  }

  private static async getJobSkills(id: string) {
    return DataBase.selectFrom('user_to_job_skill')
      .select(['job_skill_id'])
      .where('user_id', '=', id)
      .execute()
  }

  async insertLocalDB(): Promise<CompiledQuery[]> {
    const userId = this.context.id!
    const queries: CompiledQuery[] = []
    const tenant_id = useUserStore().userInfo!.tenantId
    const rawData = { ...(await this.parsed)! }
    const columns = Object.keys(userTableSchema.shape) as Array<keyof typeof userTableSchema.shape>
    const sqliteParams = {
      ...filterPick(rawData, ...columns),
      ...{ available: Number(rawData.available) }, // type conversion for sqlite3 boolean
      ...{ tenant_id }
    }
    queries.push(DataBase.insertInto(this.table).values(sqliteParams).compile())
    queries.push(
      ...this.createUserToDepartmentLocalDBQuery(
        userId,
        this.base?.departmentIds || [],
        rawData.departmentIds,
        tenant_id
      )
    )
    queries.push(
      ...this.createUserToJobTitleLocalDBQuery(
        userId,
        this.base?.jobTitleIds || [],
        rawData.jobTitleIds,
        tenant_id
      )
    )
    queries.push(
      ...this.createUserToJobSkillLocalDBQuery(
        userId,
        this.base?.jobSkillIds || [],
        rawData.jobSkillIds,
        tenant_id
      )
    )
    return queries
  }

  async updateLocalDB(): Promise<CompiledQuery[]> {
    const userId = this.context.id!
    const queries: CompiledQuery[] = []
    const tenant_id = useUserStore().userInfo!.tenantId
    const rawData = { ...(await this.parsed)! }
    const modifiedParams = pick(
      rawData,
      Object.keys(this.modified) as (keyof typeof this.modified)[]
    )
    const columns = Object.keys(userTableSchema.shape) as Array<keyof typeof userTableSchema.shape>
    const sqliteParams = Object.fromEntries(
      Object.entries({ ...filterPick(modifiedParams, ...columns) }).map(([key, value]) => [
        key,
        typeof value === 'boolean' ? Number(value) : value
      ])
    )
    if (Object.keys(sqliteParams).length > 0) {
      queries.push(
        DataBase.updateTable(this.table).set(sqliteParams).where('id', '=', userId).compile()
      )
    }
    queries.push(
      ...this.createUserToDepartmentLocalDBQuery(
        userId,
        this.base?.departmentIds || [],
        rawData.departmentIds,
        tenant_id
      )
    )
    queries.push(
      ...this.createUserToJobTitleLocalDBQuery(
        userId,
        this.base?.jobTitleIds || [],
        rawData.jobTitleIds,
        tenant_id
      )
    )
    queries.push(
      ...this.createUserToJobSkillLocalDBQuery(
        userId,
        this.base?.jobSkillIds || [],
        rawData.jobSkillIds,
        tenant_id
      )
    )
    return queries
  }

  async deleteLocalDB(): Promise<CompiledQuery[]> {
    throw Error('can not delete user')
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const tenant = (await UserMutator.getTenantData())!
    const params = (await this.parsed)!
    return addUserMutationQuery(params, tenant.firebase_tenant_id!, commit_id)
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    if (this.isNewEntity()) throw Error('This user is newly created.')
    const tenant = (await UserMutator.getTenantData())!
    const rawData = { ...(await this.parsed)! }
    const modifiedParams = pick(
      rawData,
      Object.keys(this.modified) as (keyof typeof this.modified)[]
    )
    const prev = this.base!
    return changeUserMutationQuery(
      rawData.id,
      modifiedParams,
      prev,
      tenant.firebase_tenant_id!,
      commit_id
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    throw Error('can not delete user')
  }

  private createUserToDepartmentLocalDBQuery(
    userId: string,
    beforeDepartmentIds: string[],
    afterDepartmentIds: string[],
    tenantId: string
  ) {
    return buildM2MLocalDBquery({
      mappingTable: 'user_to_department',
      fromColumn: 'user_id',
      fromId: userId,
      toColumn: 'department_id',
      beforeToIds: beforeDepartmentIds,
      afterToIds: afterDepartmentIds,
      tenantId: tenantId
    })
  }

  private createUserToJobTitleLocalDBQuery(
    userId: string,
    beforeJobTitleIds: string[],
    afterJobTitleIds: string[],
    tenantId: string
  ) {
    return buildM2MLocalDBquery({
      mappingTable: 'user_to_job_title',
      fromColumn: 'user_id',
      fromId: userId,
      toColumn: 'job_title_id',
      beforeToIds: beforeJobTitleIds,
      afterToIds: afterJobTitleIds,
      tenantId: tenantId
    })
  }

  private createUserToJobSkillLocalDBQuery(
    userId: string,
    beforeJobSkillIds: string[],
    afterJobSkillIds: string[],
    tenantId: string
  ) {
    return buildM2MLocalDBquery({
      mappingTable: 'user_to_job_skill',
      fromColumn: 'user_id',
      fromId: userId,
      toColumn: 'job_skill_id',
      beforeToIds: beforeJobSkillIds,
      afterToIds: afterJobSkillIds,
      tenantId: tenantId
    })
  }
}
